import { useNavigationContext } from "./context.js";
import { activateNode, focusNode, isFocused, joinId, registerFocusListener } from "@fiveway/core";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
//#region src/hooks.ts
/**
* React hook that returns the focused node ID for a given scope.
*
* @example
*
* ```ts
* const nav = useNavnode("container", containerHandler);
* const focusedId = useFocusedId(nav.id);
* ```
*
* @see {@link NodeId}
*/
function useFocusedId(scope) {
	const { tree, parentNode } = useNavigationContext();
	const globalId = joinId(parentNode, scope);
	const subscribe = useCallback((handler) => registerFocusListener(tree, globalId, handler), [tree, globalId]);
	return useSyncExternalStore(subscribe, () => isFocused(tree, globalId) ? tree.focus : null);
}
/**
* React hook that returns whether a given node ID is focused.
*
* @example
* ```ts
* const nav = useNavnode("layout", containerHandler);
* const isContentFocused = useIsFocused(`${nav.id}/content`);
* ```
*/
function useIsFocused(nodeId) {
	return useFocusedId(nodeId) !== null;
}
/**
* React hook that calls a function when the focus inside a given node ID changes.
*
* When the focus moves outside the given node ID, the function is called with `null`.
*
* @example
* ```ts
* const nav = useNavnode("layout", containerHandler);
* useOnFocusChange(nav.id, (id) => {
* 	console.log(id);
* });
* ```
*/
function useOnFocusChange(nodeId, handler) {
	const { tree, parentNode } = useNavigationContext();
	const globalId = joinId(parentNode, nodeId);
	const handlerRef = useRef(handler);
	handlerRef.current = handler;
	useEffect(() => {
		handlerRef.current(isFocused(tree, globalId) ? tree.focus : null);
		return registerFocusListener(tree, globalId, () => {
			const id = isFocused(tree, globalId) ? tree.focus : null;
			handlerRef.current(id);
		});
	}, [globalId, tree]);
}
/**
* React hook that calls a function when given node ID is focused.
*
* @example
* ```ts
* const nav = useNavnode("layout", containerHandler);
* useOnFocus(nav.id, () => {
* 	console.log("focus gained");
* });
* ```
*/
function useOnFocus(nodeId, handler) {
	const lastFocused = useRef(false);
	useOnFocusChange(nodeId, (id) => {
		const isFocused = id !== null;
		if (!lastFocused.current && isFocused) handler();
		lastFocused.current = isFocused;
	});
}
/**
* React hook that calls a function when given node ID loses focus.
*
* @example
* ```ts
* const nav = useNavnode("layout", containerHandler);
* useOnBlur(nav.id, () => {
* 	console.log("focus lost");
* });
* ```
*/
function useOnBlur(nodeId, handler) {
	const lastFocused = useRef(false);
	useOnFocusChange(nodeId, (id) => {
		const isFocused = id !== null;
		if (lastFocused.current && !isFocused) handler();
		lastFocused.current = isFocused;
	});
}
/**
* React hook that returns a function for focusing nodes.
* By default the function will focus nodes relative to the current parent node.
*
* @example
* ```tsx
* const focus = useFocus();
*
* <button onClick={() => focus("content")}>Focus content</button>
* ```
*/
function useFocus(scope) {
	const { tree, parentNode } = useNavigationContext();
	scope ??= parentNode;
	return useCallback((nodeId, options) => {
		focusNode(tree, joinId(scope, nodeId), options);
	}, [tree, scope]);
}
/**
* React hook that returns a function for activating nodes.
* By default the function will activate nodes relative to the current parent node.
*
* @example
* ```tsx
* const activate = useActivate();
*
* <button onClick={() => activate("content")}>Activate content</button>
* ```
*/
function useActivate(scope) {
	const { tree, parentNode } = useNavigationContext();
	scope ??= parentNode;
	return useCallback((nodeId, options) => {
		activateNode(tree, joinId(scope, nodeId), options);
	}, [tree, scope]);
}
//#endregion
export { useActivate, useFocus, useFocusedId, useIsFocused, useOnBlur, useOnFocus, useOnFocusChange };

//# sourceMappingURL=hooks.js.map